import { Card, CardContent } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/tailwind.utils'
import { Info } from 'lucide-react'
import type { ReactNode } from 'react'

const RiskProfileStatsCardLoading = ({ className }: { className?: string }) => {
  return (
    <Card className={cn('min-h-24 rounded-md shadow-sm', className)}>
      <CardContent className="flex h-full flex-col justify-between p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          <div className="h-4 w-4 animate-pulse rounded bg-muted" />
        </div>

        <div className="mt-1 space-y-2">
          <div className="h-5 w-32 animate-pulse rounded bg-muted" />
          <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        </div>
      </CardContent>
    </Card>
  )
}

export type RiskProfileStatsCardProps = {
  label: string
  value: ReactNode
  description?: ReactNode
  tooltipDescription?: string
  isLoading?: boolean
  className?: string
}

export const RiskProfileStatsCard = ({
  label,
  value,
  description,
  tooltipDescription,
  isLoading,
  className,
}: RiskProfileStatsCardProps) => {
  if (isLoading) {
    return <RiskProfileStatsCardLoading className={className} />
  }

  return (
    <Card className={cn('min-h-24 rounded-md shadow-sm', className)}>
      <CardContent className="flex h-full flex-col justify-between p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>

          {tooltipDescription !== undefined && tooltipDescription.trim() !== '' && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`${label} information`}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" align="end" className="max-w-64 text-xs">
                  {tooltipDescription}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <div className="mt-1 space-y-1">
          <div className="text-[24px] font-semibold leading-tight text-rexBlue-900">{value}</div>
          {description !== undefined && (
            <div className="text-sm leading-tight text-muted-foreground">{description}</div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
